/*
 *  Licensed under the EUPL, Version 1.2 or – as soon they will be approved by
 the European Commission - subsequent versions of the EUPL (the "Licence");
 You may not use this work except in compliance with the Licence.
 You may obtain a copy of the Licence at:

  https://joinup.ec.europa.eu/software/page/eupl

 Unless required by applicable law or agreed to in writing, software
 distributed under the Licence is distributed on an "AS IS" basis,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the Licence for the specific language governing permissions and
 limitations under the Licence. */

import { useSelector } from "react-redux";
import { getStopPermissions } from "../../../../../utils/permissionsUtils";
import { useIsViewingOlderVersion } from "../../../Shared/StopStatus";
import { RootState } from "../../types";

/**
 * Hook for managing parent stop place state from Redux
 * Provides stop place data, permissions, and loading state
 */
export const useParentStopPlaceState = () => {
  // Redux selectors
  // For freshly placed stops the data lives in `newStop` until saved; fall back to it
  // so the wizard and drawer render immediately without needing USE_NEW_STOP_AS_CURRENT.
  const stopPlace = useSelector(
    (state: RootState) =>
      state.stopPlace.current ?? (state.stopPlace as any).newStop,
  );
  const originalStopPlace = useSelector(
    (state: RootState) => state.stopPlace.originalCurrent,
  );
  const isModified = useSelector(
    (state: RootState) => state.stopPlace.stopHasBeenModified,
  );
  const versions = useSelector((state: RootState) => state.stopPlace.versions);
  const isLoading = useSelector((state: RootState) => state.stopPlace.loading);
  const activeMap = useSelector((state: RootState) => state.mapUtils.activeMap);

  // Permissions
  // An older version from the history is read-only (F-42).
  const isViewingOlderVersion = useIsViewingOlderVersion();
  const permissions = getStopPermissions(stopPlace) as any;
  const canEdit = Boolean(permissions.canEdit) && !isViewingOlderVersion;
  const canDelete =
    (permissions.canDelete || permissions.canDeleteStop || false) &&
    !isViewingOlderVersion;

  return {
    stopPlace,
    originalStopPlace,
    isModified,
    versions,
    isLoading,
    activeMap,
    canEdit,
    canDelete,
  };
};
